import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './RootStore';

export interface AppNotification {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: Record<string, any>;
}

/**
 * Con qué llegan al server la lectura y la quita de un aviso que el server guardó
 * (UserNotification). Si el server no se entera, para él sigue sin leer y lo
 * reenvía en cada conexión SSE. Lo da el RootStore solo en los proyectos con el
 * contrato de avisos (store/notificationServerSync.ts); sin él, todo queda en la
 * campana, como los avisos locales.
 */
export interface NotificationServerSync {
  /** markNotificationRead */
  markRead(userNotificationId: string): Promise<void>;
  /** markAllNotificationsRead */
  markAllRead(): Promise<void>;
  /** archiveNotification */
  archive(userNotificationId: string): Promise<void>;
}

/** Lo que se agrega a la campana: el store le pone el id local y la marca de no leído. */
export type NewAppNotification = Omit<AppNotification, 'id' | 'timestamp' | 'read'> & {
  /** Cuándo pasó. Un aviso del server trae la suya (createdAt); sin una fecha válida, la de llegada. */
  timestamp?: Date | string | null;
};

/**
 * Un aviso del server que salió de la campana: con la X (archivado) o al
 * cerrarla, ya leído. Se recuerda para que, si el server lo reenvía porque no se
 * enteró, no vuelva a mostrarse y se le mande de nuevo lo que corresponde.
 */
export interface RemovedServerNotification {
  userNotificationId: string;
  archived: boolean;
}

/**
 * Los avisos del server y los locales tienen topes separados, por fecha, y unos
 * nunca desalojan a los otros. Del server, 50: los mismos que el server reenvía
 * en cada conexión (los 50 sin leer más nuevos), así la campana llena coincide
 * con lo que reenvía. Locales, 50: el tope que tenía toda la campana, así una
 * tanda de trabajo (ediciones listas, firmas, reintentos y errores de pipelines)
 * no guarda menos que antes, y la campana queda en 100 a lo sumo.
 */
const MAX_SERVER_NOTIFICATIONS = 50;
const MAX_LOCAL_NOTIFICATIONS = 50;
const STORAGE_KEY = 'app_notifications';
const REMOVED_STORAGE_KEY = 'app_notifications_removed';

/**
 * Cuántos avisos quitados se recuerdan; pasado el tope se olvidan los más viejos.
 * Uno solo importa mientras el server lo siga teniendo sin leer (no se enteró de
 * la lectura o del archivo), y el server reenvía en cada conexión a lo sumo los
 * 50 sin leer más nuevos, los mismos que entran en la campana: 200 son cuatro
 * campanas llenas de avisos quitados antes de olvidar uno, y olvidarlo cuesta a
 * lo sumo que vuelva a mostrarse una vez. Guardados son unos 14 KB.
 */
const MAX_REMOVED = 200;

const isRemovedEntry = (value: unknown): value is RemovedServerNotification => {
  const entry = value as Partial<RemovedServerNotification> | null;
  return (
    !!entry &&
    typeof entry === 'object' &&
    typeof entry.userNotificationId === 'string' &&
    entry.userNotificationId !== '' &&
    typeof entry.archived === 'boolean'
  );
};

/**
 * El id del aviso en el server (UserNotification), si es uno que el server guardó.
 * Los avisos locales (errores y reintentos de pipelines, verificación de emails)
 * no lo tienen.
 */
const serverNotificationId = (notification: { data?: Record<string, any> }): string | undefined => {
  const id = notification.data?.userNotificationId;
  return typeof id === 'string' && id !== '' ? id : undefined;
};

/** Para ordenar por fecha: una fecha inválida (una campana guardada rota) cuenta como la más vieja. */
const timeOf = (notification: AppNotification): number => {
  const time = notification.timestamp.getTime();
  return Number.isNaN(time) ? -Infinity : time;
};

/** Los avisos del server leídos de una lista, como quitados sin archivar. */
const readServerEntries = (notifications: AppNotification[]): RemovedServerNotification[] =>
  notifications.flatMap((n) => {
    const userNotificationId = serverNotificationId(n);
    return n.read && userNotificationId ? [{ userNotificationId, archived: false }] : [];
  });

const validDate = (value: Date | string | null | undefined): Date | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Junta los avisos del server repetidos de una campana guardada (las que
 * acumularon una copia por reconexión antes de KAN-447): queda uno por
 * userNotificationId, el más antiguo (el más cerca de la fecha real), en el
 * lugar del primero, y leído si alguno lo estaba. Los locales quedan como están.
 */
const mergeServerDuplicates = (notifications: AppNotification[]): AppNotification[] => {
  const copies = new Map<string, AppNotification[]>();
  for (const n of notifications) {
    const id = serverNotificationId(n);
    if (id) copies.set(id, [...(copies.get(id) ?? []), n]);
  }
  const merged: AppNotification[] = [];
  const done = new Set<string>();
  for (const n of notifications) {
    const id = serverNotificationId(n);
    if (!id) {
      merged.push(n);
      continue;
    }
    if (done.has(id)) continue;
    done.add(id);
    const group = copies.get(id) ?? [n];
    const oldest = group.reduce((a, b) => (b.timestamp.getTime() < a.timestamp.getTime() ? b : a));
    merged.push({ ...oldest, read: group.some((c) => c.read) });
  }
  return merged;
};

export class NotificationStore {
  notifications: AppNotification[] = [];
  private isLoading: boolean = true;
  /**
   * Los avisos del server quitados de la campana, del más viejo al más nuevo.
   * Fuera de MobX: un arreglo observable no se puede guardar en IndexedDB.
   */
  private removed: RemovedServerNotification[] = [];

  constructor(private rootStore: RootStore, private serverSync?: NotificationServerSync) {
    makeAutoObservable<NotificationStore, 'serverSync' | 'removed'>(this, { serverSync: false, removed: false });
    this.loadNotifications();
  }

  get unreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  get sortedNotifications(): AppNotification[] {
    return [...this.notifications].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  addNotification(notification: NewAppNotification): void {
    // El server manda los avisos sin leer en cada conexión SSE, y el cliente
    // reconecta cada vez que renueva el token: uno que ya está en la campana no se
    // agrega de nuevo (y si ya se leyó, sigue leído), ni uno que se quitó.
    const userNotificationId = serverNotificationId(notification);
    if (userNotificationId && this.resolvedHere(userNotificationId)) {
      return;
    }

    const { timestamp, ...rest } = notification;
    const newNotification: AppNotification = {
      ...rest,
      id: crypto.randomUUID(),
      timestamp: validDate(timestamp) ?? new Date(),
      read: false,
    };

    // El tope de su clase (del server o local) deja afuera a los más viejos por
    // fecha, no por orden de llegada: con el del server la campana llena coincide
    // con lo que el server reenvía, y un reenvío no agrega ni desaloja nada. Uno
    // más viejo que todos los de su clase con el tope lleno no entra.
    let kept = [newNotification, ...this.notifications];
    const fromServer = userNotificationId !== undefined;
    const sameClass = kept.filter((n) => (serverNotificationId(n) !== undefined) === fromServer);
    const max = fromServer ? MAX_SERVER_NOTIFICATIONS : MAX_LOCAL_NOTIFICATIONS;
    if (sameClass.length > max) {
      const newest = new Set([...sameClass].sort((a, b) => timeOf(b) - timeOf(a)).slice(0, max));
      if (!newest.has(newNotification)) {
        return;
      }
      const leaving = new Set(sameClass.filter((n) => !newest.has(n)));
      // Un leído del server que sale por el tope se recuerda como uno que salió al
      // cerrar la campana: si el server no se enteró de la lectura, lo cura el reenvío.
      this.rememberRemoved(readServerEntries([...leaving]));
      kept = kept.filter((n) => !leaving.has(n));
    }

    runInAction(() => {
      this.notifications = kept;
    });

    this.saveNotifications();
  }

  /**
   * Si la campana ya tiene o ya quitó este aviso del server. Si el server lo
   * reenvía igual es que no se enteró de lo que pasó acá: se le vuelve a mandar,
   * un intento por reenvío (si falla, el próximo reenvío lo vuelve a intentar).
   */
  private resolvedHere(userNotificationId: string): boolean {
    const inBell = this.notifications.find((n) => serverNotificationId(n) === userNotificationId);
    if (inBell) {
      if (inBell.read) {
        this.tellServer('markNotificationRead', (sync) => sync.markRead(userNotificationId));
      }
      return true;
    }
    const removed = this.removed.find((r) => r.userNotificationId === userNotificationId);
    if (removed) {
      if (removed.archived) {
        this.tellServer('archiveNotification', (sync) => sync.archive(userNotificationId));
      } else {
        this.tellServer('markNotificationRead', (sync) => sync.markRead(userNotificationId));
      }
      return true;
    }
    return false;
  }

  /** Recuerda avisos quitados; uno que ya estaba pasa a ser el más nuevo. */
  private rememberRemoved(entries: RemovedServerNotification[]): void {
    if (entries.length === 0) return;
    const ids = new Set(entries.map((e) => e.userNotificationId));
    this.removed = [...this.removed.filter((r) => !ids.has(r.userNotificationId)), ...entries].slice(-MAX_REMOVED);
  }

  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    const wasUnread = !!notification && !notification.read;
    runInAction(() => {
      if (notification) {
        notification.read = true;
      }
    });
    this.saveNotifications();

    // La campana llama a esto en cada clic (abre y cierra el texto): al server
    // va solo el que pasa a leído.
    const userNotificationId = notification && serverNotificationId(notification);
    if (wasUnread && userNotificationId) {
      this.tellServer('markNotificationRead', (sync) => sync.markRead(userNotificationId));
    }
  }

  markAllAsRead(): void {
    const serverUnread = this.notifications.some(n => !n.read && serverNotificationId(n));
    runInAction(() => {
      this.notifications.forEach(n => {
        n.read = true;
      });
    });
    this.saveNotifications();

    // Con solo avisos locales sin leer no hay nada que marcar en el server.
    if (serverUnread) {
      this.tellServer('markAllNotificationsRead', (sync) => sync.markAllRead());
    }
  }

  /** La X de la campana: un aviso del server se archiva, para que no vuelva. */
  deleteNotification(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    const userNotificationId = notification && serverNotificationId(notification);
    runInAction(() => {
      this.notifications = this.notifications.filter(n => n.id !== id);
    });
    if (userNotificationId) {
      this.rememberRemoved([{ userNotificationId, archived: true }]);
    }
    this.saveNotifications();

    if (userNotificationId) {
      this.tellServer('archiveNotification', (sync) => sync.archive(userNotificationId));
    }
  }

  clearAll(): void {
    runInAction(() => {
      this.notifications = [];
    });
    this.saveNotifications();
  }

  /** Al cerrar la campana: salen los leídos, sin archivarlos. */
  clearRead(): void {
    const leaving = this.notifications.filter(n => n.read);
    runInAction(() => {
      this.notifications = this.notifications.filter(n => !n.read);
    });
    this.rememberRemoved(readServerEntries(leaving));
    this.saveNotifications();
  }

  private async loadNotifications(): Promise<void> {
    try {
      const [stored, storedRemoved] = await Promise.all([
        this.rootStore.persistor.get<AppNotification[]>(STORAGE_KEY),
        this.rootStore.persistor.get<unknown>(REMOVED_STORAGE_KEY),
      ]);
      // Lo que no se entiende se ignora.
      this.removed = Array.isArray(storedRemoved)
        ? storedRemoved
            .filter(isRemovedEntry)
            .map(({ userNotificationId, archived }) => ({ userNotificationId, archived }))
            .slice(-MAX_REMOVED)
        : [];
      if (stored && Array.isArray(stored)) {
        const loaded = stored.map(n => ({
          ...n,
          timestamp: new Date(n.timestamp),
        }));
        const merged = mergeServerDuplicates(loaded);
        runInAction(() => {
          this.notifications = merged;
          this.isLoading = false;
        });
        // La campana que tenía copias queda guardada limpia.
        if (merged.length < loaded.length) {
          this.saveNotifications();
        }
      } else {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    } catch (error) {
      console.error('[NotificationStore] Failed to load notifications:', error);
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Avisa al server de lo que ya cambió en la campana. Si falla, se loguea y nada
   * más: la campana no vuelve atrás, y el server, que lo sigue teniendo sin leer,
   * lo reenvía en la próxima conexión, donde no se repite (ya está en la campana
   * o se recuerda quitado) y se le vuelve a mandar (resolvedHere).
   */
  private async tellServer(
    operation: string,
    call: (sync: NotificationServerSync) => Promise<void>
  ): Promise<void> {
    if (!this.serverSync) return;
    try {
      await call(this.serverSync);
    } catch (error) {
      console.warn(`[NotificationStore] ${operation} failed:`, error);
    }
  }

  private async saveNotifications(): Promise<void> {
    if (this.isLoading) return;

    try {
      await Promise.all([
        this.rootStore.persistor.set(STORAGE_KEY, this.notifications),
        this.rootStore.persistor.set(REMOVED_STORAGE_KEY, this.removed),
      ]);
    } catch (error) {
      console.error('[NotificationStore] Failed to save notifications:', error);
    }
  }
}
